import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { TimeoutError, catchError, defer, finalize, tap, throwError, timeout } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class ApiConnectionState {
  private readonly pending = signal(0);
  private requests = 0;
  private responded = false;
  private connectionError: string | null = null;
  readonly loading = computed(() => this.pending() > 0);
  readonly error = signal<string | null>(null);

  start(isUserRequest: boolean): void {
    if (this.requests === 0) {
      this.responded = false;
      this.connectionError = null;
    }
    this.requests++;
    if (isUserRequest) this.pending.update(count => count + 1);
  }
  received(): void {
    this.responded = true;
    this.error.set(null);
  }
  failed(message: string): void { this.connectionError = message; }
  finish(isUserRequest: boolean): void {
    if (isUserRequest) this.pending.update(count => count - 1);
    this.requests--;
    if (this.requests === 0 && !this.responded && this.connectionError) {
      this.error.set(this.connectionError);
    }
  }
  retry(): void { this.error.set(null); }
}

export const apiConnectionInterceptor: HttpInterceptorFn = (request, next) => {
  if (!request.url.startsWith(`${environment.apiUrl}/`)) return next(request);
  const state = inject(ApiConnectionState);
  const isUserRequest = request.method === 'GET' && request.url.split('?')[0] === `${environment.apiUrl}/me`;
  return defer(() => {
    state.start(isUserRequest);
    const response = next(request);
    const result = isUserRequest
      ? response.pipe(timeout({ each: 30000 })) : response;
    return result.pipe(
      tap(event => { if (event instanceof HttpResponse) state.received(); }),
      catchError((error: unknown) => {
        if (error instanceof TimeoutError) {
          if (isUserRequest) state.failed('La API está tardando demasiado en responder. Volvé a intentarlo.');
        } else if (error instanceof HttpErrorResponse) {
          if ([0, 502, 503, 504].includes(error.status)) {
            if (isUserRequest) state.failed('No se pudo conectar con la API. Verificá tu conexión y volvé a intentarlo.');
          } else {
            state.received();
          }
        }
        return throwError(() => error);
      }),
      finalize(() => state.finish(isUserRequest)),
    );
  });
};
